import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import './App.scss';
import AppRoutes from './AppRoutes';
import Navbar from './shared/Navbar';
import Sidebar from './shared/Sidebar';
import SettingsPanel from './shared/SettingsPanel';
import Footer from './shared/Footer';
import { withTranslation } from "react-i18next";
import { applySidebarIconOnlyPreference } from './sidebarMode';
import { startBillingSessionWatch, stopBillingSessionWatch, recordBillingActivity } from './billingSessionTimeout';
import { LOGIN_PATH, performLogout, clearAllSessions, isPhpSessionExpiredMessage } from './authLogout';

const FULL_PAGE_LAYOUT_ROUTES = [
  '/user-pages/login-1',
  '/user-pages/login-2',
  '/user-pages/register-1',
  '/user-pages/register-2',
  '/user-pages/lockscreen',
  '/error-pages/error-404',
  '/error-pages/error-500',
  '/general-pages/landing-page',
];

class App extends Component {
  state = {}
  componentDidMount() {
    this.onPhpSessionExpiredMessage = (event) => {
      if (!isPhpSessionExpiredMessage(event)) {
        return;
      }
      // PHP idle session expired inside an embedded iframe → full React sign-out.
      performLogout(this.props.history, { reason: 'php-session-expired' });
    };
    window.addEventListener('message', this.onPhpSessionExpiredMessage);
    this.onRouteChanged();
  }

  componentWillUnmount() {
    if (this.onPhpSessionExpiredMessage) {
      window.removeEventListener('message', this.onPhpSessionExpiredMessage);
    }
  }

  isFullPagePath(pathname) {
    return FULL_PAGE_LAYOUT_ROUTES.includes(pathname);
  }

  /**
   * If auth was cleared (idle timeout / logout) but the SPA stayed on a
   * protected route, force navigation to the login page.
   */
  ensureAuthenticatedOrRedirect(pathname) {
    const isLoggedIn = !!localStorage.getItem('UserId');
    if (isLoggedIn || this.isFullPagePath(pathname)) {
      return false;
    }
    try {
      this.props.history.replace(LOGIN_PATH);
    } catch (e) {
      window.location.replace(window.location.origin + '/?logged_out=' + Date.now() + '#/user-pages/login-1');
    }
    return true;
  }

  syncBillingSessionWatch(isFullPageLayout) {
    const isLoggedIn = !!localStorage.getItem('UserId');
    const isFullPage =
      typeof isFullPageLayout === 'boolean' ? isFullPageLayout : this.state.isFullPageLayout;
    if (!isFullPage && isLoggedIn) {
      startBillingSessionWatch(this.props.history);
    } else {
      stopBillingSessionWatch();
    }
  }

  applyDefaultSidebarCollapse(isFullPageLayout) {
    const body = document.querySelector('body');
    if (!body) {
      return;
    }
    if (isFullPageLayout) {
      body.classList.remove('sidebar-icon-only');
      body.classList.remove('sidebar-fixed');
    } else {
      body.classList.add('sidebar-fixed');
      applySidebarIconOnlyPreference();
    }
  }
  render () {
    const pathname = this.props.location && this.props.location.pathname;
    const isLoggedIn = !!localStorage.getItem('UserId');
    const isFullPage = this.isFullPagePath(pathname) || this.state.isFullPageLayout;
    // Never keep the authenticated chrome mounted without a session.
    const showChrome = isLoggedIn && !isFullPage;

    let navbarComponent = showChrome ? <Navbar/> : '';
    let sidebarComponent = showChrome ? <Sidebar/> : '';
    let SettingsPanelComponent = showChrome ? <SettingsPanel/> : '';
    let footerComponent = showChrome ? <Footer/> : '';
    return (
      <div className="container-scroller">
        { navbarComponent }
        <div className="container-fluid page-body-wrapper" style={{paddingLeft: 0}}>
          { sidebarComponent }
          <div className="main-panel">
            <div className="content-wrapper">
              <AppRoutes/>
              { SettingsPanelComponent }
            </div>
            { footerComponent }
          </div>
        </div>
      </div>
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.onRouteChanged();
    } else if (!localStorage.getItem('UserId') && !this.isFullPagePath(this.props.location.pathname)) {
      // Auth cleared in-place (idle logout) without a route change yet.
      this.ensureAuthenticatedOrRedirect(this.props.location.pathname);
    }
  }

  onRouteChanged() {
    // console.log("ROUTE CHANGED");
    const { i18n } = this.props;
    const body = document.querySelector('body');
    if(this.props.location.pathname === '/layout/RtlLayout') {
      body.classList.add('rtl');
      i18n.changeLanguage('ar');
    }
    else {
      body.classList.remove('rtl')
      i18n.changeLanguage('en');
    }
    window.scrollTo(0, 0);
    const pathname = this.props.location.pathname;
    // Opening the login route always wipes an existing session (no redirect loop).
    if (pathname === LOGIN_PATH && localStorage.getItem('UserId')) {
      clearAllSessions({ reason: 'login-page' });
    }
    if (this.ensureAuthenticatedOrRedirect(pathname)) {
      this.setState({ isFullPageLayout: true });
      return;
    }
    const isFullPage = this.isFullPagePath(pathname);
    this.setState({ isFullPageLayout: isFullPage });
    const pageBodyWrapper = document.querySelector('.page-body-wrapper');
    if (pageBodyWrapper) {
      if (isFullPage) {
        pageBodyWrapper.classList.add('full-page-wrapper');
      } else {
        pageBodyWrapper.classList.remove('full-page-wrapper');
      }
    }
    this.applyDefaultSidebarCollapse(isFullPage);
    if (!isFullPage && localStorage.getItem('UserId')) {
      recordBillingActivity();
    }
    this.syncBillingSessionWatch(isFullPage);
  }

}

export default withTranslation()(withRouter(App));
